import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { AuthState, User, LoginCredentials } from '@/types'
import { apiClient } from '@/lib/api/client'
import { TwoFactorService } from '@/lib/services/twoFactorService'

interface AuthActions {
  login: (credentials: LoginCredentials) => Promise<{ success: boolean; twoFactorRequired?: boolean; twoFactorSetupRequired?: boolean; message?: string }>
  /**
   * @param retainTicketIds - omit/undefined for status defaults (session timeout);
   *   pass an array (including []) for explicit retain choice from the logout modal.
   */
  logout: (retainTicketIds?: number[]) => Promise<void>
  setUser: (user: User) => void
  setToken: (token: string) => void
  setLoading: (loading: boolean) => void
  forgotPassword: (email: string) => Promise<void>
  resetPassword: (data: { token: string; password: string; password_confirmation: string }) => Promise<void>
  initializeAuth: () => Promise<void>
  verifyTwoFactor: (email: string, code: string, rememberMe?: boolean) => Promise<{ success: boolean; user?: User; token?: string; message?: string }>
  enable2FASetup: (email: string, password: string) => Promise<{ success: boolean; twoFactorRequired?: boolean; message?: string }>
  refreshUser: () => Promise<void>
}

export const useAuthStore = create<AuthState & AuthActions>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      rememberMe: false,

      login: async (credentials: LoginCredentials) => {
        set({ isLoading: true })
        try {
          const response = await apiClient.post('/login', credentials)
          
          // Debug: Log response structure to help diagnose API differences
          if (import.meta.env.DEV) {
            console.log('[AuthStore] Login response:', {
              hasData: !!response.data,
              dataKeys: response.data ? Object.keys(response.data) : [],
              hasToken: !!(response.data?.token),
              hasUser: !!(response.data?.user),
              responseStructure: response.data
            })
          }
          
          if (response.data.two_factor_setup_required) {
            set({
              isLoading: false,
              rememberMe: credentials.remember_me ?? false,
            })
            return {
              success: false,
              twoFactorSetupRequired: true,
              twoFactorRequired: false,
              message: response.data.message
            }
          }
          
          if (response.data.two_factor_required) {
            set({
              isLoading: false,
              rememberMe: credentials.remember_me ?? false,
            })
            return {
              success: false,
              twoFactorRequired: true,
              twoFactorSetupRequired: false,
              message: response.data.message
            }
          }
          
          // Handle different response structures
          // Some APIs return { data: { token, user } }, others return { token, user } directly
          const responseData = response.data?.data || response.data
          const { token, user } = responseData
          
          if (!token || !user) {
            console.error('[AuthStore] Login response missing token or user:', responseData)
            throw new Error('Invalid login response: missing token or user')
          }
          
          const userToStore = {
            ...user,
            user_role: (user as any)?.user_role || user?.user_role,
            user_routes: Array.isArray(user?.user_routes) ? user.user_routes : [],
          }
          
          set({
            user: userToStore as User,
            token,
            isAuthenticated: true,
            isLoading: false,
            rememberMe: credentials.remember_me ?? false,
          })

          // Set user status to online upon login (sync from API response or set locally)
          import('@/store/userStatus').then(({ useUserStatusStore }) => {
            const statusFromApi = userToStore?.availability_status
            if (statusFromApi) {
              // Sync from API response
              useUserStatusStore.setState({
                status: statusFromApi as import('@/lib/userAvailability').UserAvailabilityStatus,
                statusChangedAt: userToStore.status_changed_at || null,
              })
            } else {
              // If API doesn't return status, set to online (backend should have set it)
              useUserStatusStore.setState({
                status: 'online',
                statusChangedAt: new Date().toISOString(),
              })
            }
          }).catch(() => {})
          
          if (import.meta.env.DEV) {
            console.log('[AuthStore] Login successful, token stored:', {
              hasToken: !!token,
              hasUser: !!userToStore,
              userRoutesCount: userToStore.user_routes?.length || 0
            })
          }
          
          return {
            success: true,
            twoFactorRequired: false
          }
        } catch (error: any) {
          set({ isLoading: false })
          
          // Log detailed error for debugging
          if (import.meta.env.DEV) {
            console.error('[AuthStore] Login error:', {
              message: error.message,
              response: error.response?.data,
              status: error.response?.status,
              code: error.code,
              isNetworkError: !error.response
            })
          }
          
          if (error.response?.status === 403 && error.response?.data?.two_factor_setup_required) {
            set({
              rememberMe: credentials.remember_me ?? false,
            })
            return {
              success: false,
              twoFactorSetupRequired: true,
              twoFactorRequired: false,
              message: error.response.data.message || 'Two-factor authentication setup is required.'
            }
          }
          
          if (error.response?.status === 200 && error.response?.data?.two_factor_required) {
            set({
              rememberMe: credentials.remember_me ?? false,
            })
            return {
              success: false,
              twoFactorRequired: true,
              twoFactorSetupRequired: false,
              message: error.response.data.message || 'Two-factor authentication is required.'
            }
          }
          
          throw error
        }
      },

      logout: async (retainTicketIds?: number[]) => {
        try {
          // Best-effort Vicidial session teardown before ATLAS logout.
          const { teardownVicidialSession } = await import('@/lib/vicidial')
          teardownVicidialSession()
        } catch (_) {
          // Ignore; proceed with logout
        }
        try {
          // Mark user as offline before logout (so Agent Status reflects correctly)
          const { UserService } = await import('@/lib/services/userService')
          await UserService.updateStatus('offline').catch(() => {})
        } catch (_) {
          // Ignore; proceed with logout
        }
        try {
          // Omit body when undefined so idle logout applies status defaults server-side.
          if (retainTicketIds !== undefined) {
            await apiClient.post('/logout', { retain_ticket_ids: retainTicketIds })
          } else {
            await apiClient.post('/logout')
          }
        } catch (error) {
          console.error('[AuthStore] Logout API call failed:', error)
        } finally {
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            rememberMe: false,
          })
          if (typeof window !== 'undefined') {
            localStorage.removeItem('auth-storage')
          }
          // Set local status to offline so persisted store and next load show offline until API sync
          import('@/store/userStatus').then(({ useUserStatusStore }) => {
            useUserStatusStore.setState({ status: 'offline', statusChangedAt: null })
          }).catch(() => {})
        }
      },

      setUser: (user: User) => {
        set({ user })
      },

      setToken: (token: string) => {
        set({ token, isAuthenticated: true })
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading })
      },

      forgotPassword: async (email: string) => {
        set({ isLoading: true })
        try {
          await apiClient.post('/forgot-password', { email })
          set({ isLoading: false })
        } catch (error) {
          set({ isLoading: false })
          throw error
        }
      },

      resetPassword: async (data: { token: string; password: string; password_confirmation: string }) => {
        set({ isLoading: true })
        try {
          await apiClient.post('/auth/reset-password', data)
          set({ isLoading: false })
        } catch (error) {
          set({ isLoading: false })
          throw error
        }
      },

      initializeAuth: async () => {
        set({ isLoading: true })
        try {
          const state = get()

          // Check if we have persisted auth data
          if (state.token && state.user) {
            // Preserve existing user_routes in case API doesn't return them or returns empty
            const existingUserRoutes = state.user?.user_routes || []

            // Set authenticated state first (optimistic)
            set({ isAuthenticated: true })

            // Refresh user data to ensure we have latest user_routes
            // This is important because localStorage might have stale data
            try {
              const response = await apiClient.post('/user/me', { application: 'admin-support' })
              const raw = response?.data
              const user = (raw && typeof raw === 'object' && raw.data) ? raw.data : raw

              // Preserve existing routes if API returns empty or doesn't include user_routes
              const apiUserRoutes = Array.isArray(user?.user_routes) ? user.user_routes : []
              const finalUserRoutes = apiUserRoutes.length > 0 ? apiUserRoutes : existingUserRoutes

              if (import.meta.env.DEV) {
                console.log('[initializeAuth] User routes:', {
                  existingCount: existingUserRoutes.length,
                  apiCount: apiUserRoutes.length,
                  finalCount: finalUserRoutes.length,
                  usingExisting: finalUserRoutes === existingUserRoutes
                })
              }

              const userToStore = {
                ...(user && typeof user === 'object' ? user : {}),
                user_routes: Array.isArray(finalUserRoutes) ? finalUserRoutes : [],
              }
              set({
                user: userToStore as User,
                isAuthenticated: true,
                isLoading: false
              })

              // Sync user availability status from API response
              if (userToStore?.availability_status) {
                // Use dynamic import to avoid circular dependency
                import('@/store/userStatus').then(({ useUserStatusStore }) => {
                  const statusStore = useUserStatusStore.getState()
                  // Only update if API has a different status or timestamp
                  if (statusStore.status !== userToStore.availability_status || !statusStore.statusChangedAt) {
                    // Update status without API call (already synced from server)
                    useUserStatusStore.setState({
                      status: userToStore.availability_status as import('@/lib/userAvailability').UserAvailabilityStatus,
                      statusChangedAt: userToStore.status_changed_at || null
                    })
                  }
                }).catch(() => {
                  // Ignore errors - status store might not be initialized yet
                })
              }
            } catch (error: any) {
              console.error('Failed to refresh user on init:', error)

              // Handle different error types
              if (error.response?.status === 401) {
                // Token is invalid - clear auth
                set({
                  user: null,
                  token: null,
                  isAuthenticated: false,
                  isLoading: false,
                  rememberMe: false,
                })
                if (typeof window !== 'undefined') {
                  localStorage.removeItem('auth-storage')
                }
              } else if (!error.response) {
                // Network error (CORS, timeout, etc.) - don't clear auth
                // Token might still be valid, just can't reach the server
                // Keep existing user data including routes
                console.warn('Network error during auth initialization. Keeping existing auth state.')
                set({ isLoading: false })
              } else {
                // Other errors (500, etc.) - don't clear auth
                // Server error doesn't mean token is invalid
                // Keep existing user data including routes
                set({ isLoading: false })
              }
            }
          } else {
            // No persisted auth data
            set({
              user: null,
              token: null,
              isAuthenticated: false,
              isLoading: false
            })
          }
        } finally {
          if (get().isLoading) {
            set({ isLoading: false })
          }
        }
      },

      verifyTwoFactor: async (email: string, code: string, rememberMe?: boolean) => {
        set({ isLoading: true })
        try {
          const result = await TwoFactorService.verifyCode(email, code, rememberMe)
          
          if (result.success && result.user && result.token) {
            set({
              user: result.user,
              token: result.token,
              isAuthenticated: true,
              isLoading: false,
              rememberMe: rememberMe ?? false,
            })

            // Set user status to online upon login (after 2FA verification)
            import('@/store/userStatus').then(({ useUserStatusStore }) => {
              const statusFromApi = result.user?.availability_status
              if (statusFromApi) {
                // Sync from API response
                useUserStatusStore.setState({
                  status: statusFromApi as import('@/lib/userAvailability').UserAvailabilityStatus,
                  statusChangedAt: result.user.status_changed_at || null,
                })
              } else {
                // If API doesn't return status, set to online (backend should have set it)
                useUserStatusStore.setState({
                  status: 'online',
                  statusChangedAt: new Date().toISOString(),
                })
              }
            }).catch(() => {})
            
            return {
              success: true,
              user: result.user,
              token: result.token
            }
          } else {
            set({ isLoading: false })
            return {
              success: false,
              message: result.message || 'Verification failed'
            }
          }
        } catch (error: any) {
          set({ isLoading: false })
          return {
            success: false,
            message: error.message || 'Verification failed'
          }
        }
      },

      enable2FASetup: async (email: string, password: string) => {
        set({ isLoading: true })
        try {
          const response = await apiClient.post('/auth/enable-2fa-setup', { email, password })
          
          if (response.data.success && response.data.two_factor_required) {
            set({ isLoading: false })
            return {
              success: true,
              twoFactorRequired: true,
              message: response.data.message
            }
          } else {
            set({ isLoading: false })
            return {
              success: false,
              message: response.data.message || 'Failed to enable 2FA'
            }
          }
        } catch (error: any) {
          set({ isLoading: false })
          return {
            success: false,
            message: error.response?.data?.message || 'Failed to enable 2FA'
          }
        }
      },

      refreshUser: async () => {
        try {
          const response = await apiClient.post('/user/me', { application: 'admin-support' })
          const raw = response?.data
          const user = (raw && typeof raw === 'object' && raw.data) ? raw.data : raw
          const userToStore = {
            ...(user && typeof user === 'object' ? user : {}),
            user_routes: Array.isArray(user?.user_routes) ? user.user_routes : [],
          }
          set({ user: userToStore as User })
        } catch (error) {
          console.error('Failed to refresh user:', error)
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        rememberMe: state.rememberMe,
      }),
    }
  )
)

